import { Box, Typography } from "@mui/material";
import React from "react";
import { Agent } from "../../../Shared-ui/Types/Agent";
import { BaseUrl, ChatBaseUrl, RedirectUrl } from "../../../Constant/Setting";

interface PublicApiProps {
  agent: Agent;
}

const PublicApi: React.FC<PublicApiProps> = ({ agent }) => {
  const configuredBase = BaseUrl || ChatBaseUrl || RedirectUrl || (typeof window !== 'undefined' ? window.location.origin : "");
  const baseUrl = configuredBase?.replace(/\/$/, "");
  const apiPrefix = baseUrl?.endsWith("/api") ? "" : "/api";
  const endpoint = `${baseUrl}${apiPrefix}/agents/${agent.id}/chat`;

  const curlExample = `curl -X POST \
  '${endpoint}?stream=false' \
  -H 'X-API-Key: YOUR_API_KEY' \
  -H 'Content-Type: application/json' \
  -d '{
    "message": "Hello",
    "conversation_id": null,
    "metadata": { "user_context": { "id": "visitor-123" } }
  }'`;

  const curlStreamingExample = `curl -X POST \
  '${endpoint}?stream=true' \
  -H 'X-API-Key: YOUR_API_KEY' \
  -H 'Content-Type: application/json' \
  -d '{
    "message": "Hello",
    "conversation_id": null,
    "metadata": { "user_context": { "id": "visitor-123" } }
  }'`;

  const fetchExample = `const BASE_URL = '${baseUrl}';
fetch(\`${'${'}BASE_URL}/api/agents/${agent.id}/chat?stream=false\`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    message: 'Hello',
    conversation_id: null,
    metadata: { user_context: { id: 'visitor-123' } }
  })
}).then(r => r.json()).then(console.log);`;

  const fetchStreamingExample = `const BASE_URL = '${baseUrl}';
const response = await fetch(\`${'${'}BASE_URL}/api/agents/${agent.id}/chat?stream=true\`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    message: 'Hello',
    conversation_id: null,
    metadata: { user_context: { id: 'visitor-123' } }
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  
  const chunk = decoder.decode(value);
  const lines = chunk.split('\\n');
  
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6);
      if (data === '[DONE]') return;
      
      try {
        const parsed = JSON.parse(data);
        console.log('Stream chunk:', parsed);
      } catch (e) {
        // Skip invalid JSON
      }
    }
  }
}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle1">Endpoint</Typography>
      <Box sx={{ fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {endpoint}
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>Streaming Parameter</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Add <code>?stream=true</code> to enable streaming responses, or <code>?stream=false</code> for regular JSON responses (default: false).
      </Typography>

      <Typography variant="subtitle1">cURL - Regular Response (Default)</Typography>
      <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {curlExample}
      </Box>

      <Typography variant="subtitle1">cURL - Streaming Response</Typography>
      <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {curlStreamingExample}
      </Box>

      <Typography variant="subtitle1">JavaScript - Regular Response (Default)</Typography>
      <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {fetchExample}
      </Box>

      <Typography variant="subtitle1">JavaScript - Streaming Response</Typography>
      <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {fetchStreamingExample}
      </Box>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>Streaming Response Format</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        When streaming is enabled, responses are sent as Server-Sent Events (SSE) with the following format:
      </Typography>
      <Box sx={{ fontFamily: 'monospace', p: 2, bgcolor: '#0e1116', color: '#eaeef3', borderRadius: 1, overflowX: 'auto' }}>
        {`data: {"response": "chunk of text", "conversation_id": "uuid", "is_complete": false, "metadata": {...}}
data: {"response": "more text", "conversation_id": "uuid", "is_complete": false, "metadata": {...}}
data: {"response": "final chunk", "conversation_id": "uuid", "is_complete": true, "metadata": {...}}
data: [DONE]`}
      </Box>
    </Box>
  );
};

export default PublicApi;




